import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useProjects } from "../resume/useResumeData";
import { HiXMark, HiPlus } from "react-icons/hi2";
import Modal from "../../ui/Modal";
import FileInputPreview from "../../ui/FileInputPreview";
import { getImageUrl } from "../../services/api";

const ProjectsModal = ({ itemToEdit, onClose }) => {
    const { addProject, updateProject, isAdding, isUpdating } = useProjects();
    const { register, control, handleSubmit, reset } = useForm({
        defaultValues: {
            title: "",
            description: "",
            image: "",
            link: "",
            github_link: "",
            category: "Web",
            technologies: [{ value: "" }], // Using field array for tags
            tags: [{ value: "" }]
        },
    });

    // Field arrays for flexible inputs
    const { fields: techFields, append: appendTech, remove: removeTech } = useFieldArray({
        control,
        name: "technologies"
    });

    const { fields: tagFields, append: appendTag, remove: removeTag } = useFieldArray({
        control,
        name: "tags"
    });

    useEffect(() => {
        if (itemToEdit) {
            reset({
                ...itemToEdit,
                technologies: itemToEdit.technologies?.map(t => ({ value: t })) || [{ value: "" }],
                tags: itemToEdit.tags?.map(t => ({ value: t })) || [{ value: "" }]
            });
        }
    }, [itemToEdit, reset]);

    const onSubmit = (data) => {
        const formData = new FormData();

        // Append simple fields
        formData.append('title', data.title);
        formData.append('description', data.description || '');
        formData.append('link', data.link || '');
        formData.append('github_link', data.github_link || '');
        formData.append('category', data.category || 'Web');

        // Append Techs and Tags as JSON strings
        const technologies = data.technologies.map(t => t.value).filter(t => t);
        const tags = data.tags.map(t => t.value).filter(t => t);

        formData.append('technologies', JSON.stringify(technologies));
        formData.append('tags', JSON.stringify(tags));

        // Handle Image
        // If it's a file list (from file input) and has a file
        if (data.image && data.image.length > 0 && data.image[0] instanceof File) {
            formData.append('image', data.image[0]);
        } else if (itemToEdit && itemToEdit.image) {
            // Keep existing image if not changed (handled by backend not overwriting if no file)
            // But if we want to support clearing image? 
            // For now, if no new file, backend keeps old. 
            // We don't append 'image' if it's just the old url string, unless we handle it in backend as string?
            // Backend middleware checks req.file. 
            // Routes check req.body.image as fallback string. 
        }

        if (itemToEdit) {
            updateProject({ id: itemToEdit.id, formData }, { onSuccess: onClose });
            // Note: updateProject in local apiResume service needs to handle FormData correctly.
            // Usually axios detects FormData and sets Content-Type.
            // But verify useResumeData -> apiResume -> apiRequest behavior.
        } else {
            addProject(formData, { onSuccess: onClose });
        }
    };

    const isSubmitting = isAdding || isUpdating;

    return (
        <Modal
            isLoading={isSubmitting}
            onClose={onClose}
            title={itemToEdit ? "Edit Project" : "Add New Project"}
            size="large"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 font-primary text-white">
                {/* Basic Info */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <label className="mb-2 block text-sm font-medium text-stone-300">Project Title</label>
                        <input
                            {...register("title", { required: "Title is required" })}
                            className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
                            placeholder="e.g. Portfolio CMS"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-stone-300">Category</label>
                        <input
                            {...register("category")}
                            className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
                            placeholder="e.g. Web Development"
                        />
                    </div>
                </div>

                {/* Links */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <label className="mb-2 block text-sm font-medium text-stone-300">Live Demo Link</label>
                        <input
                            {...register("link")}
                            type="url"
                            className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
                            placeholder="https://..."
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-stone-300">GitHub/Code Link</label>
                        <input
                            {...register("github_link")}
                            type="url"
                            className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
                            placeholder="https://github.com/..."
                        />
                    </div>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-medium text-stone-300">Project Image</label>
                    <FileInputPreview
                        registerProps={register("image")}
                        existingImageUrl={itemToEdit?.image ? getImageUrl(itemToEdit.image) : null}
                        wrapperClassName="relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-600 bg-black/20 hover:bg-black/30"
                        inputClassName="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        previewClassName="absolute right-2 top-2 h-16 w-16 rounded-lg object-cover border border-stone-600"
                    >
                        <div className="flex flex-col items-center text-stone-400">
                            <svg className="mb-2 h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                            <span className="text-sm">Click to upload image</span>
                        </div>
                    </FileInputPreview>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-medium text-stone-300">Description</label>
                    <textarea
                        {...register("description")}
                        rows="4"
                        className="w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
                        placeholder="Describe the project..."
                    />
                </div>

                {/* Technologies */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-stone-300">Technologies Used</label>
                    <div className="flex flex-wrap gap-3">
                        {techFields.map((field, index) => (
                            <div key={field.id} className="relative group">
                                <input
                                    {...register(`technologies.${index}.value`)}
                                    className="w-32 rounded-lg border-none bg-black/30 px-3 py-2 text-sm text-stone-200 outline-none focus:ring-2 focus:ring-primary placeholder:text-stone-500"
                                    placeholder="React"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeTech(index)}
                                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-600"
                                >
                                    <HiXMark className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => appendTech({ value: "" })}
                            className="flex items-center gap-1 rounded-lg border border-dashed border-stone-600 px-3 py-2 text-sm text-stone-400 hover:border-primary hover:text-primary transition-colors"
                        >
                            <HiPlus className="h-4 w-4" /> Add Tech
                        </button>
                    </div>
                </div>

                {/* Tags */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-stone-300">Tags</label>
                    <div className="flex flex-wrap gap-3">
                        {tagFields.map((field, index) => (
                            <div key={field.id} className="relative group">
                                <input
                                    {...register(`tags.${index}.value`)}
                                    className="w-32 rounded-lg border-none bg-black/30 px-3 py-2 text-sm text-stone-200 outline-none focus:ring-2 focus:ring-primary placeholder:text-stone-500"
                                    placeholder="Featured"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeTag(index)}
                                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-600"
                                >
                                    <HiXMark className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => appendTag({ value: "" })}
                            className="flex items-center gap-1 rounded-lg border border-dashed border-stone-600 px-3 py-2 text-sm text-stone-400 hover:border-primary hover:text-primary transition-colors"
                        >
                            <HiPlus className="h-4 w-4" /> Add Tag
                        </button>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn flex min-w-[8rem] items-center justify-center border-none bg-gradient-to-tr from-sky-600 to-cyan-400 py-3 font-semibold uppercase text-white shadow-lg hover:from-cyan-400 hover:to-sky-600 active:scale-95 disabled:opacity-50 transition-all rounded-lg"
                    >
                        {isSubmitting ? "Saving..." : "Save Project"}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ProjectsModal;
